import { useEffect, useState } from "react";
import { Menu, ArrowRight, ArrowUpRight, Phone, Mail, MapPin, Clock, Truck, Wrench, PackageCheck, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LionMark } from "@/components/LionMark";
import { CareersForm } from "@/sections/CareersForm";
import { QuoteForm } from "@/sections/QuoteForm";
import { CONTACT, STATUS_PILLS, MARQUEE_ITEMS, STATS, SERVICES, MANIFESTO, GALLERY, TESTIMONIALS, CAREERS } from "@/data/content";
import { useCountUp } from "@/hooks/useCountUp";

const NAV = [
  { id: "services", label: "Services" },
  { id: "standard", label: "The Standard" },
  { id: "fleet", label: "Fleet & Shop" },
  { id: "voices", label: "Voices" },
  { id: "careers", label: "Careers" },
  { id: "contact", label: "Contact" },
];

function TopNav({ onQuoteClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="top-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[hsl(var(--lh-ink))]/85 backdrop-blur-md border-b border-[hsl(var(--lh-bone))]/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a href="#top" onClick={(e)=>{e.preventDefault(); window.scrollTo({top:0, behavior:"smooth"});}} className="flex items-center gap-3 group" data-testid="brand-logo">
          <LionMark size={44} />
          <span className="flex flex-col leading-[0.9]">
            <span className="font-mark font-black text-[1.55rem] uppercase tracking-[0.005em] text-[hsl(var(--lh-bone))]">
              Lionhead
            </span>
            <span className="font-mono text-[0.58rem] uppercase tracking-[0.28em] text-[hsl(var(--lh-bone))]/55 mt-[3px]">
              International
            </span>
          </span>
        </a>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => scrollTo(n.id)}
              data-testid={`nav-${n.id}`}
              className="font-mono text-[0.72rem] uppercase tracking-[0.18em] text-[hsl(var(--lh-bone))]/70 hover:text-[hsl(var(--lh-gold))] transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button onClick={onQuoteClick} className="lh-btn-primary hidden md:inline-flex" data-testid="nav-quote-btn">
            Get a Quote <ArrowRight size={14} strokeWidth={2.5} />
          </button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 -mr-2 text-[hsl(var(--lh-bone))]" data-testid="mobile-menu-btn" aria-label="Open menu">
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[hsl(var(--lh-ink))] border-l border-[hsl(var(--lh-bone))]/10 text-[hsl(var(--lh-bone))] w-full sm:w-[380px]">
              <div className="pt-12 flex flex-col gap-6">
                {NAV.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => scrollTo(n.id)}
                    className="text-left font-display text-3xl hover:text-[hsl(var(--lh-gold))] transition-colors"
                    data-testid={`mobile-nav-${n.id}`}
                  >
                    {n.label}
                  </button>
                ))}
                <div className="lh-rule my-4" />
                <a
                  href={`tel:${CONTACT.phoneTel}`}
                  className="flex items-center gap-3 text-[hsl(var(--lh-bone))] hover:text-[hsl(var(--lh-gold))] transition-colors"
                  data-testid="mobile-dispatch-phone"
                >
                  <Phone size={16} className="text-[hsl(var(--lh-gold))]" strokeWidth={1.5} />
                  <span className="font-mono text-[0.72rem] uppercase tracking-[0.18em] opacity-60">Dispatch</span>
                  <span className="font-display text-xl ml-auto">{CONTACT.phone}</span>
                </a>
                <button onClick={()=>{setOpen(false); onQuoteClick();}} className="lh-btn-primary self-start" data-testid="mobile-quote-btn">
                  Get a Quote <ArrowRight size={14} strokeWidth={2.5}/>
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function Hero({ onQuoteClick, onServicesClick }) {
  return (
    <section id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-24 pb-16 md:pb-20">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1592805144716-feeccccef5ac?w=2400&q=85&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--lh-ink))] via-[hsl(var(--lh-ink))]/70 to-[hsl(var(--lh-ink))]/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--lh-ink))]/80 via-transparent to-[hsl(var(--lh-ink))]/40" />
      </div>

      <div className="relative max-w-[1400px] mx-auto w-full px-6 md:px-10">
        <div className="flex items-center gap-3 mb-8 lh-fade-up">
          <span className="lh-eyebrow">Est. Lilburn, GA</span>
          <span className="h-px flex-1 max-w-[120px] bg-[hsl(var(--lh-bone))]/30" />
          <span className="font-mono text-[0.68rem] uppercase tracking-[0.2em] text-[hsl(var(--lh-bone))]/50">Volume 26 / Winter</span>
        </div>

        <h1 className="font-display text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] tracking-tight max-w-[16ch] mb-8 lh-fade-up" data-testid="hero-headline" style={{animationDelay:"120ms"}}>
          Powering American Roads.
          <span className="block italic text-[hsl(var(--lh-gold))]">Repairing the fleets</span>
          <span className="block">that build it.</span>
        </h1>

        <p className="max-w-xl text-[hsl(var(--lh-bone))]/75 text-lg md:text-xl leading-relaxed mb-10 lh-fade-up" style={{animationDelay:"260ms"}}>
          A family-run freight, logistics, and heavy-duty repair operation out of Lilburn, Georgia — moving loads coast to coast and keeping the iron running when it matters most.
        </p>

        <div className="flex flex-wrap items-center gap-4 mb-12 lh-fade-up" style={{animationDelay:"400ms"}}>
          <button onClick={onQuoteClick} className="lh-btn-primary" data-testid="hero-quote-btn">
            Get a Quote <ArrowRight size={14} strokeWidth={2.5} />
          </button>
          <button onClick={onServicesClick} className="lh-btn-ghost" data-testid="hero-services-btn">
            Explore Services
          </button>
        </div>

        <div className="flex flex-wrap gap-3 lh-fade-up" style={{animationDelay:"540ms"}}>
          {STATUS_PILLS.map((p) => (
            <span key={p.label} className="lh-pill" data-testid={`pill-${p.label.toLowerCase().replace(/\s+/g,'-')}`}>
              {p.live && <span className="dot" />} {p.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="border-y border-[hsl(var(--lh-bone))]/10 bg-[hsl(var(--lh-ink-2))] overflow-hidden py-6" data-testid="marquee">
      <div className="flex whitespace-nowrap lh-marquee-track">
        {items.map((it, i) => (
          <span key={`${i}-${it}`} className="mx-8 font-display italic text-2xl md:text-4xl text-[hsl(var(--lh-bone))]/70 flex items-center gap-8">
            {it}
            <span className="w-2 h-2 rounded-full bg-[hsl(var(--lh-gold))]" />
          </span>
        ))}
      </div>
    </div>
  );
}

function StatItem({ stat }) {
  const { ref, display } = useCountUp(stat.value, {
    duration: 1800,
    decimals: stat.value % 1 !== 0 ? 1 : 0,
  });
  return (
    <div ref={ref} className="p-8 md:p-10 border-r border-b border-[hsl(var(--lh-bone))]/10 last:border-r-0 md:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r lg:[&:nth-child(4n)]:border-r-0" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="font-display text-5xl md:text-7xl font-black leading-none text-[hsl(var(--lh-gold))]">
        {stat.prefix || ""}{display}{stat.suffix || ""}
      </div>
      <div className="mt-4 font-mono text-[0.72rem] uppercase tracking-[0.2em] text-[hsl(var(--lh-bone))]/60">
        {stat.label}
      </div>
    </div>
  );
}

function Stats() {
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-[hsl(var(--lh-bone))]/10">
        {STATS.map((s) => (
          <StatItem key={s.label} stat={s} />
        ))}
      </div>
    </section>
  );
}

function Services({ onRequest }) {
  const icons = { freight: Truck, logistics: PackageCheck, repair: Wrench };
  return (
    <section id="services" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid md:grid-cols-[1fr_2fr] gap-8 md:gap-16 items-end mb-16">
        <div>
          <span className="lh-eyebrow">What We Do</span>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-4">
            Three <span className="italic text-[hsl(var(--lh-gold))]">pillars.</span> One operation.
          </h2>
        </div>
        <p className="text-[hsl(var(--lh-bone))]/70 text-lg md:text-xl leading-relaxed max-w-2xl md:justify-self-end">
          Freight moved on our own trucks. Supply chains orchestrated by dispatchers who&apos;ve driven them. Heavy-duty repair backed by mechanics who&apos;ve overhauled every diesel worth naming.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 md:gap-8">
        {SERVICES.map((s) => {
          const Icon = icons[s.id];
          return (
            <article key={s.id} className="lh-card-hover group flex flex-col border border-[hsl(var(--lh-bone))]/10 bg-[hsl(var(--lh-ink-2))] hover:border-[hsl(var(--lh-gold))]/40 transition-colors" data-testid={`service-card-${s.id}`}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--lh-ink-2))] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 lh-pill !bg-[hsl(var(--lh-ink))]/80">{s.kicker}</span>
                <Icon className="absolute bottom-4 right-4 text-[hsl(var(--lh-gold))]" size={28} strokeWidth={1.5}/>
              </div>
              <div className="p-6 md:p-8 flex flex-col flex-1">
                <h3 className="font-display text-3xl md:text-4xl leading-tight mb-2">{s.title}</h3>
                <p className="text-[hsl(var(--lh-bone))]/60 mb-6 italic font-display">{s.tagline}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-3 text-[hsl(var(--lh-bone))]/85 text-sm md:text-base">
                      <ChevronRight size={16} className="text-[hsl(var(--lh-gold))] mt-1 flex-shrink-0" strokeWidth={2.5}/>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => onRequest(s.formValue)} className="lh-btn-ghost self-start" data-testid={`service-cta-${s.id}`}>
                  Request this service <ArrowUpRight size={14} strokeWidth={2.5}/>
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section id="standard" className="bg-[hsl(var(--lh-ink-2))] border-y border-[hsl(var(--lh-bone))]/10 py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-16 md:mb-24 max-w-3xl">
          <span className="lh-eyebrow">The Lionhead Standard</span>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-4">
            A manifesto, <span className="italic text-[hsl(var(--lh-gold))]">not a slogan.</span>
          </h2>
        </div>
        <div className="space-y-16 md:space-y-24">
          {MANIFESTO.map((m, i) => (
            <div key={m.num} className={`grid md:grid-cols-[auto_1fr_2fr] gap-6 md:gap-12 items-start ${i % 2 === 1 ? "md:pl-16" : ""}`} data-testid={`manifesto-${m.num}`}>
              <div className="lh-chapter-num">{m.num}</div>
              <h3 className="font-display text-3xl md:text-5xl leading-tight tracking-tight max-w-[10ch]">{m.title}</h3>
              <p className="text-[hsl(var(--lh-bone))]/75 text-lg md:text-xl leading-relaxed max-w-2xl">{m.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  return (
    <section id="fleet" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="mb-12 md:mb-16 grid md:grid-cols-2 gap-6 items-end">
        <div>
          <span className="lh-eyebrow">Fleet & Shop</span>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-4">
            Where the <span className="italic text-[hsl(var(--lh-gold))]">work</span> lives.
          </h2>
        </div>
        <p className="text-[hsl(var(--lh-bone))]/70 text-lg md:justify-self-end max-w-md">
          Iron in the yard. Wrenches in the bay. Ports on the horizon. This is the operation, without the stock-photo gloss.
        </p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {GALLERY.map((g, i) => (
          <figure key={g.caption} className="lh-card-hover overflow-hidden border border-[hsl(var(--lh-bone))]/10" data-testid={`gallery-${i}`}>
            <div className="aspect-[4/5] overflow-hidden">
              <img src={g.image} alt={g.caption} className="w-full h-full object-cover" />
            </div>
            <figcaption className="p-4 font-mono text-[0.7rem] uppercase tracking-[0.16em] text-[hsl(var(--lh-bone))]/70 bg-[hsl(var(--lh-ink-2))]">
              {String(i + 1).padStart(2, "0")} · {g.caption}
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section id="voices" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="mb-12 md:mb-16">
        <span className="lh-eyebrow">Voices from the Yard</span>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-4 max-w-3xl">
          Receipts, not <span className="italic text-[hsl(var(--lh-gold))]">reviews.</span>
        </h2>
      </div>
      <div className="grid md:grid-cols-3 gap-6 md:gap-8">
        {TESTIMONIALS.map((t, i) => (
          <blockquote key={t.name} className="p-8 border border-[hsl(var(--lh-bone))]/10 bg-[hsl(var(--lh-ink-2))]" data-testid={`testimonial-${i}`}>
            <div className="font-display italic text-6xl text-[hsl(var(--lh-gold))] leading-none mb-4">&ldquo;</div>
            <p className="text-[hsl(var(--lh-bone))]/90 text-lg leading-relaxed mb-8">{t.quote}</p>
            <footer className="pt-6 border-t border-[hsl(var(--lh-bone))]/15">
              <div className="font-display text-lg">{t.name}</div>
              <div className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-[hsl(var(--lh-bone))]/55 mt-1">{t.role}</div>
            </footer>
          </blockquote>
        ))}
      </div>
    </section>
  );
}

function CareersSection() {
  return (
    <section id="careers" className="bg-[hsl(var(--lh-ink-2))] border-y border-[hsl(var(--lh-bone))]/10 py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <span className="lh-eyebrow">Careers</span>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-4 mb-8">
              Drive it. <span className="italic text-[hsl(var(--lh-gold))]">Fix it.</span> Own the shift.
            </h2>
            <p className="text-[hsl(var(--lh-bone))]/70 text-lg mb-12 max-w-lg">
              We hire slow and pay honest. If you show up on time and give a damn about the equipment, there is a chair here for you.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {[CAREERS.driver, CAREERS.mechanic].map((c) => (
                <div key={c.title}>
                  <h3 className="font-display text-2xl mb-4 border-b border-[hsl(var(--lh-bone))]/15 pb-3">{c.title}</h3>
                  <ul className="space-y-3">
                    {c.benefits.map((b) => (
                      <li key={b} className="flex gap-3 text-sm text-[hsl(var(--lh-bone))]/85">
                        <span className="text-[hsl(var(--lh-gold))] font-mono">→</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <CareersForm />
        </div>
      </div>
    </section>
  );
}

function Contact({ selectedService, setSelectedService }) {
  return (
    <section id="contact" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-16 lg:gap-24">
        <div>
          <span className="lh-eyebrow">Contact</span>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-4 mb-10">
            Send a load. <span className="italic text-[hsl(var(--lh-gold))]">Book a bay.</span>
          </h2>

          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <Mail className="text-[hsl(var(--lh-gold))] mt-1" size={20} strokeWidth={1.5}/>
              <div>
                <div className="lh-label mb-1">General</div>
                <a href={`mailto:${CONTACT.email}`} className="font-display text-2xl hover:text-[hsl(var(--lh-gold))] transition-colors" data-testid="contact-email">{CONTACT.email}</a>
                <div className="text-[hsl(var(--lh-bone))]/60 mt-1">Dispatch: {CONTACT.dispatchEmail}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="text-[hsl(var(--lh-gold))] mt-1" size={20} strokeWidth={1.5}/>
              <div>
                <div className="lh-label mb-1">24/7 Dispatch</div>
                <a href={`tel:${CONTACT.phoneTel}`} className="font-display text-2xl hover:text-[hsl(var(--lh-gold))] transition-colors" data-testid="contact-phone">{CONTACT.phone}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="text-[hsl(var(--lh-gold))] mt-1" size={20} strokeWidth={1.5}/>
              <div>
                <div className="lh-label mb-1">Headquarters</div>
                <div className="font-display text-xl">{CONTACT.address.line1}</div>
                <div className="text-[hsl(var(--lh-bone))]/80">{CONTACT.address.line2}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Clock className="text-[hsl(var(--lh-gold))] mt-1" size={20} strokeWidth={1.5}/>
              <div>
                <div className="lh-label mb-1">Hours</div>
                <div className="text-[hsl(var(--lh-bone))]/90">{CONTACT.hours.office}</div>
                <div className="text-[hsl(var(--lh-gold))]">{CONTACT.hours.emergency}</div>
              </div>
            </div>
          </div>
        </div>

        <QuoteForm selectedService={selectedService} onServiceHandled={() => setSelectedService("")} />
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[hsl(var(--lh-bone))]/10 bg-[hsl(var(--lh-ink-2))]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <div className="grid md:grid-cols-4 gap-10 mb-16">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <LionMark size={54} />
              <span className="flex flex-col leading-[0.9]">
                <span className="font-mark font-black text-[1.9rem] uppercase tracking-[0.005em] text-[hsl(var(--lh-bone))]">
                  Lionhead
                </span>
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-[hsl(var(--lh-bone))]/55 mt-[3px]">
                  International
                </span>
              </span>
            </div>
            <p className="text-[hsl(var(--lh-bone))]/60 max-w-md">
              Family-run freight, logistics, and heavy-duty repair — headquartered in Lilburn, Georgia. Powering American roads since day one.
            </p>
          </div>
          <div>
            <div className="lh-label mb-4">Navigate</div>
            <ul className="space-y-2">
              {NAV.map((n) => (
                <li key={n.id}><a href={`#${n.id}`} className="hover:text-[hsl(var(--lh-gold))] transition-colors">{n.label}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="lh-label mb-4">Reach us</div>
            <ul className="space-y-2 text-[hsl(var(--lh-bone))]/85">
              <li><a href={`mailto:${CONTACT.dispatchEmail}`} className="hover:text-[hsl(var(--lh-gold))]">{CONTACT.dispatchEmail}</a></li>
              <li><a href={`tel:${CONTACT.phoneTel}`} className="hover:text-[hsl(var(--lh-gold))]" data-testid="footer-phone">{CONTACT.phone}</a></li>
              <li>{CONTACT.address.line1}</li>
              <li>{CONTACT.address.line2}</li>
            </ul>
          </div>
        </div>
        <div className="lh-thick-rule mb-6" />
        <div className="flex flex-wrap justify-between gap-4 font-mono text-[0.7rem] uppercase tracking-[0.16em] text-[hsl(var(--lh-bone))]/50">
          <span>© {new Date().getFullYear()} Lionhead International, LLC</span>
          <span>Volume 26 · Lilburn, GA · MC# 12-8451</span>
        </div>
      </div>
    </footer>
  );
}

export default function Home() {
  const [selectedService, setSelectedService] = useState("");

  const scrollToContact = (service) => {
    if (service) setSelectedService(service);
    setTimeout(() => {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <div className="text-[hsl(var(--lh-bone))] font-body" data-testid="home-page">
      <TopNav onQuoteClick={() => scrollToContact()} />
      <Hero onQuoteClick={() => scrollToContact()} onServicesClick={() => document.getElementById("services")?.scrollIntoView({behavior:"smooth"})} />
      <Marquee />
      <Stats />
      <Services onRequest={scrollToContact} />
      <Manifesto />
      <Gallery />
      <Testimonials />
      <CareersSection />
      <Contact selectedService={selectedService} setSelectedService={setSelectedService} />
      <Footer />
    </div>
  );
}
